import {
  DefaultAlertDialog,
  DefaultModalDialog,
  DefaultScreenTileBox,
  DurationViewer,
  Option,
  PictureUploadButton,
  PictureViewer,
  Select,
  Text,
  LabeledText,
} from '~/common/components/defaultUIComponents';
import { Flex, Grid, GridItem, IconButton, Tooltip } from '@chakra-ui/react';
import { RoutineApi } from '~/common/api/zeiterfassungServerApi/RoutineApi';
import EmployeeApi from '~/common/api/zeiterfassungServerApi/EmployeeApi';
import { Employee } from '~/common/entities/Department/DepartmentTypes';
import { PictureType } from '~/common/entities/Pictures/PictureTypes';
import { momentDurationToSeconds } from '~/common/utils/timeUtils';
import { Routine } from '~/common/entities/Routines/RoutineTypes';
import EmployeesInTile from '~/common/components/EmployeesInTile';
import React, { useCallback, useMemo, useState } from 'react';
import { formatName } from '~/common/utils/employeeUtils';
import { RoutineStatistics } from './RoutineStatistics';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import routes from '~/common/constants/routes.json';
import useTranslate from '~/common/utils/translate';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@chakra-ui/toast';
import _ from 'lodash';

interface RoutineInput {
  routine: Routine;
}

const RoutineTile: React.FC<RoutineInput> = ({ routine }: RoutineInput) => {
  const [showEmployeesEdit, setShowEmployeesEdit] = useState(false);
  const [{ data: employees }] = EmployeeApi.hooks.useEmployeeQuery();
  const { mutateAsync: updateRoutine } =
    RoutineApi.hooks.useUpdateRoutineMutation();
  const { mutateAsync: deleteRoutine } =
    RoutineApi.hooks.useDeleteRoutineMutation();

  const employeesOfRoutine = useMemo(() => {
    if (!employees || !routine) {
      return [];
    }
    return (
      _.map(routine.employees, (e) =>
        _.find(employees, (em) => em.id === e.id),
      ) ?? []
    );
  }, [routine, employees]);

  const employeesNotInRoutine = useMemo(() => {
    if (!employees || !routine) {
      return [];
    }
    return (
      _.filter(
        employees,
        (e) => !_.find(routine.employees, (em) => em.id === e.id),
      ) ?? []
    );
  }, [routine, employees]);

  const allEmployeesValue = 'all';
  const noneEmployeesValue = 'none';

  const [openDelete, setOpenDelete] = useState(false);
  const [openPictures, setOpenPictures] = useState(false);

  const navigate = useNavigate();
  const translate = useTranslate();

  const toggleOpenDelete = () => {
    setOpenDelete(!openDelete);
  };

  const toast = useToast({ status: 'error', title: 'generic.error' });

  const toggleOpenPictures = () => {
    setOpenPictures(!openPictures);
  };

  const toggleShowEmployeesEdit = () => {
    setShowEmployeesEdit(!showEmployeesEdit);
  };

  const handleDelete = () => {
    deleteRoutine(routine).then(toggleOpenDelete).catch(toast);
  };

  const handleAdd = useCallback(
    (value: string) => {
      if (
        value != '' &&
        routine.employees &&
        !routine.employees.find((e) => e.id == value)
      ) {
        const req = {
          id: routine.id,
          name: routine.name,
          duration: routine.duration,
          description: routine.description,
          employees: [] as Array<string>,
        };
        if (value === allEmployeesValue) {
          req.employees = employees?.map((ae) => ae?.id) ?? [];
          updateRoutine(req).catch(toast);
        } else if (value === noneEmployeesValue) {
          updateRoutine(req).catch(toast);
        } else {
          req.employees = routine.employees.map((e) => e.id);
          req.employees.push(value);
          updateRoutine(req).catch(toast);
        }
      }
    },
    [routine, employees],
  );

  const handleRemove = useCallback(
    (employee: Employee) => {
      const req = {
        id: routine.id,
        name: routine.name,
        duration: routine.duration,
        description: routine.description,
        employees: routine.employees
          ? (routine.employees
              .filter((e) => e.id !== employee.id)
              .map((e) => e.id) ?? [])
          : [],
      };
      updateRoutine(req).catch(toast);
    },
    [routine],
  );

  return (
    <DefaultScreenTileBox pt={2}>
      <Grid templateColumns="repeat(8, 1fr)" columnGap={1}>
        <GridItem colSpan={6}>
          <Text fontSize="large" fontWeight="bold">
            {routine.name}
          </Text>
          <DurationViewer
            label="routineTile.duration.label"
            labelProps={{ fontWeight: 'normal' }}
            durationInSeconds={momentDurationToSeconds(routine.duration)}
          />
        </GridItem>
        <GridItem>
          <PictureViewer
            pictureIds={routine.routine_pictures}
            projectOrJobId={routine.id}
            pictureType={PictureType.ROUTINE}
            showInModal={false}
            mt={2}
            mr={2}
            cursor="pointer"
            onClick={toggleOpenPictures}
            maxWidthPreview="9vh"
            maxHeightPreview="9vh"
          />
        </GridItem>
        <DefaultModalDialog
          dialogHeaderText="routineTile.showPictures.dialogHeaderText"
          submitText="routineTile.showPictures.submitText"
          isOpen={openPictures}
          onCancel={toggleOpenPictures}
          onSubmit={toggleOpenPictures}
          showCancelButton={false}
          size="4xl"
          initialValues={{}}
        >
          <PictureViewer
            pictureIds={routine.routine_pictures}
            projectOrJobId={routine.id}
            pictureType={PictureType.ROUTINE}
            closeModal={toggleOpenPictures}
          />
        </DefaultModalDialog>
        <GridItem justifyContent="flex-end">
          <Flex>
            <Tooltip
              label={translate('routineTile.routineEdit.tooltip.label')}
              aria-label={translate('routineTile.routineEdit.tooltip.label')}
            >
              <IconButton
                icon={<EditIcon />}
                onClick={() => {
                  navigate(routes.EDIT_ROUTINE.replace(':id', routine.id));
                }}
                aria-label={
                  translate('routineTile.routineEdit.tooltip.label') as string
                }
                isRound
                mr={1}
              />
            </Tooltip>
            <Tooltip
              label={translate('routineTile.routineDelete.tooltip.label')}
              aria-label={translate('routineTile.routineDelete.tooltip.label')}
            >
              <IconButton
                icon={<DeleteIcon />}
                onClick={toggleOpenDelete}
                aria-label={
                  translate('routineTile.routineDelete.tooltip.label') as string
                }
                isRound
                mr={1}
              />
            </Tooltip>
            <PictureUploadButton
              pictureType={PictureType.ROUTINE}
              projectOrJobId={routine.id}
            />
          </Flex>
        </GridItem>
        {routine.description && (
          <GridItem colSpan={8}>
            <LabeledText label="routineTile.description.label">
              {routine.description}
            </LabeledText>
          </GridItem>
        )}
        <GridItem />
        <DefaultAlertDialog
          dialogHeaderText="routineTile.routineDelete.dialogHeaderText"
          dialogBodyText="routineTile.routineDelete.dialogBodyText"
          isOpen={openDelete}
          onConfirmedAction={handleDelete}
          onCancel={toggleOpenDelete}
        />
        <GridItem mt={2} colSpan={8}>
          {/*Employees*/}
          <Flex align="center" mb={3}>
            <Text fontWeight="semibold" fontSize="lg" mr={5}>
              routineTile.employees.header
            </Text>
            <Tooltip
              label={translate('routineTile.employees.edit.tooltip.label')}
              aria-label={translate('routineTile.employees.edit.tooltip.label')}
            >
              <IconButton
                icon={<EditIcon />}
                onClick={toggleShowEmployeesEdit}
                aria-label={
                  translate(
                    'routineTile.employees.edit.tooltip.label',
                  ) as string
                }
                isRound
                mr={1}
              />
            </Tooltip>
            <DefaultModalDialog
              dialogHeaderText="routineTile.employees.edit.dialogHeaderText"
              submitText="routineTile.employees.edit.submitText"
              isOpen={showEmployeesEdit}
              onCancel={toggleShowEmployeesEdit}
              onSubmit={toggleShowEmployeesEdit}
              showCancelButton={false}
              size="xl"
              initialValues={{}}
            >
              <Flex justify="space-between">
                <Select
                  value=""
                  onChange={(e) => handleAdd(e.currentTarget.value)}
                  w="100%"
                  ml={60}
                  mb={2}
                >
                  <Option disabled={true} value="">
                    {translate(
                      'routineTile.employees.edit.addButton.tooltip.label',
                    )}
                  </Option>
                  <Option value={allEmployeesValue}>
                    routineTile.employees.edit.selectAll.text
                  </Option>
                  {_.orderBy(employeesNotInRoutine, formatName)?.map(
                    (employee) => (
                      <Option key={employee.id} value={employee.id}>
                        {formatName(employee)}
                      </Option>
                    ),
                  )}
                </Select>
              </Flex>
              <EmployeesInTile
                employees={employeesOfRoutine as Employee[]}
                routine={routine}
                tooltipLabel=""
                dialogHeaderText="routineTile.employees.edit.delete.dialogHeaderText"
                dialogBodyText="routineTile.employees.edit.delete.dialogBoyText"
                onRemoveEmployee={handleRemove}
              />
            </DefaultModalDialog>
          </Flex>
        </GridItem>
        <GridItem colSpan={8}>
          <RoutineStatistics routine={routine} />
        </GridItem>
      </Grid>
    </DefaultScreenTileBox>
  );
};

export default RoutineTile;
